// ./src/components/generic/QrScanner/QrScanner.tsx
//
// A self-contained camera surface that decodes QR codes AND 1D barcodes (EAN/UPC/
// Code128/Code39/ITF, …) from the live video stream and calls onDecode with the
// decoded text. Built for the door check-in console AND the inventory scan station.
//
// We manage the MediaStream ourselves (getUserMedia → attach → play), and a per-start
// generation guard makes it robust to React StrictMode double-invoking the effect
// (two getUserMedia racing over one <video> was the black/stall culprit).
//
// ⚠⚠ IT DECODES ONLY WHAT THE OPERATOR CAN SEE (ROADMAP #65 d, owner 2026-09-28). Each attempt
// crops the frame to the preview — or, with `decodeArea="guide"`, to the framing guide — through
// the same `object-fit: cover` mapping the <video> is drawn with (`scanGeometry.ts`), and decodes
// only that crop. It used to hand ZXing the WHOLE frame, so a barcode above or below the one in the
// guide, or one the preview had cropped away entirely, could win.
//
// ⚠ AND IT TRIES HARDER TO READ WHAT IS THERE (#65 e): an HD stream (the browser's default is often
// 640×480), continuous focus where the camera offers it, a torch button where it has one, and ONE
// decoder on every device — zxing-wasm (zxing-cpp as WebAssembly), every 1D and 2D symbology it
// knows. ⚠⚠ This is a PWA: on an iPhone it runs in WebKit, which has no `BarcodeDetector`, so what
// we ship is all it gets. Measured against ZXing-JS (what shipped before) and the browser's native
// detector on a synthetic harness, see `scanDecode.ts`: 109 of 126 cases vs 60 and 96, at a few ms
// a decode. It is loaded LAZILY, the first time a scanner opens, and cached after (`barcodeReader.ts`).
// Which readings are ACCEPTED — the longest of several, fragments dropped, short formats
// confirmed twice, UPC-A in its stored 12-digit form — is `scanDecode.ts`, run by the guard.
//
// ⚠⚠ ONE SCAN, THEN THE NEXT (owner, 2026-11-02: "scans non-stop and rescans already scanned code or code right close to it").
// A reading counts only when it was seen twice (not swept past), is the one nearest the aim, and passes `passScanGate`: a
// code stays spent until it has been OUT OF SIGHT for a moment, and after any scan nothing counts for a moment. A lookup in
// progress, or `paused` (the operator has something to finish), holds it.
//
// The camera button opens a drawer naming the cameras, with the one in use marked. The choice is remembered in
// localStorage. `processing` shows a "detected — looking up" overlay while the caller resolves a scanned code; each decode
// briefly flashes the frame green, and `result` then colours it with what the scan came to.
//
// ⚠ A door phone stays awake while the camera runs, and the camera comes back when the app does: iOS ends the track when
// the app goes to the background, which left a black viewfinder that looked broken.
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, Center, Group, Loader, Stack, Switch, Text, ActionIcon, Tooltip, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { ReaderOptions } from "zxing-wasm/reader";

import { AppDrawer } from "../AppDrawer";
import { AppIcon } from "../AppIcon";
import { loadBarcodeReader } from "./barcodeReader";
import { cameraChoices } from "./cameraChoices";
import { ATTEMPT_CYCLE, OPEN_SCAN_GATE, confirmReading, noteScanSighting, normalizeScanText, passScanGate, pickBarcode, rearmScanGate, resumeScanGate } from "./scanDecode";
import { ScanSoundKind, scanSoundOn, setScanSoundOn } from "./scanFeedback";
import { ScanDecodeArea, ScanGuide, decodeRegion, decodeSize, guideRect, previewRectToVideo } from "./scanGeometry";
import { flattenLighting, rotateCanvas } from "./scanImage";

// Remembered camera choice — survives reloads; reads/writes are guarded (private
// windows / blocked site-data can throw or return null).
const DEVICE_STORAGE_KEY = "em:qrscanner:deviceId";

const readStoredDeviceId = (): string | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(DEVICE_STORAGE_KEY) : null;
  } catch {
    return null;
  }
};

const writeStoredDeviceId = (id: string | null): void => {
  try {
    if (typeof window === "undefined") {
      return;
    }
    if (id) {
      window.localStorage.setItem(DEVICE_STORAGE_KEY, id);
    } else {
      window.localStorage.removeItem(DEVICE_STORAGE_KEY);
    }
  } catch {
    // storage unavailable — the picker still works for this session
  }
};

/**
 * zxing-wasm reading options. `tryRotate` covers 90°/180°/270° and `tryInvert` light-on-dark codes;
 * slants in between are the straightened attempts of `ATTEMPT_CYCLE`. Up to four symbols, so a
 * fragment returned beside the real code can be told apart (`pickBarcode`). `Plain` text, so a code
 * reads as its raw content — the form every stored code was scanned in — not a formatted rendering.
 */
const READER_OPTIONS: ReaderOptions = {
  formats: [],
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: true,
  textMode: "Plain",
  maxNumberOfSymbols: 4,
};

export interface QrScannerProps {
  /** When false the camera is released; when true it starts (and restarts on switch). */
  active: boolean;
  /** Called with the decoded text once per code (deduped within a short cooldown). */
  onDecode: (text: string) => void;
  /** Called with a translatable reason key when the camera can't start. */
  onError?: (reasonKey: string) => void;
  /** Video area height. */
  height?: number | string;
  /**
   * Framing-guide shape. "square" (default) suits QR; "wide" draws a horizontal
   * frame that matches 1D product barcodes.
   */
  guide?: ScanGuide;
  /**
   * Which part of the picture may produce a code. "visible" (default) decodes everything the
   * preview shows and nothing it hides; "guide" decodes only inside the frame — for a shelf of
   * barcodes where the neighbours above and below must not be read.
   */
  decodeArea?: ScanDecodeArea;
  /** The caller is resolving a just-scanned code → show a "detected, looking up" overlay. */
  processing?: boolean;
  /**
   * The operator has something to finish before the next code (Scandit: pause between scans when input is needed). The
   * picture stays on; nothing counts, and the words say why.
   */
  paused?: string | null;
  /** What the last scan came to: colours the frame for a moment. A new object is a new result. */
  result?: { kind: ScanSoundKind } | null;
  /** A new value forgets the last code: the same one may be scanned again now (the mode changed, the operator cancelled). */
  rearmKey?: string | number | null;
  /** How long a spent code must be out of sight. Omit where scanning is non-stop; shorter where each scan is confirmed. */
  sameCodeGoneMs?: number;
  /** Offer the "beep on each scan" switch in the camera drawer. */
  soundSwitch?: boolean;
  /** Drawn over the picture (the mode, the counters). */
  children?: React.ReactNode;
}

// Shortest pause between ticks. ⚠ Also at least TWICE what the last tick took, so a slow phone never
// spends more than about a third of its main thread decoding — the check-in screens have a search
// box people type into while the camera runs. A tick is two zxing-wasm decodes (~5–20ms measured).
const SCAN_INTERVAL_MS = 150;
const SCAN_BUSY_RATIO = 2;
// The widest crop handed to the decoder. Measured: decoding the crop at up to 1280px rather than
// 800 recovered small Code 128 / Code 39 codes (+6 of 126) — at a few ms a decode it is affordable.
const MAX_DECODE_WIDTH = 1280;
// Ask for an HD stream — the browser's default is often 640×480, too coarse for a small barcode.
const VIDEO_QUALITY: MediaTrackConstraints = { width: { ideal: 1920 }, height: { ideal: 1080 } };

export const QrScanner: React.FC<QrScannerProps> = ({
  active,
  onDecode,
  onError,
  height = 300,
  guide = "square",
  decodeArea = "visible",
  processing = false,
  paused = null,
  result = null,
  rearmKey = null,
  sameCodeGoneMs,
  soundSwitch = false,
  children,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gateRef = useRef(OPEN_SCAN_GATE);
  const startGenRef = useRef(0);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the callbacks — and the geometry the decode loop reads — in refs so the camera effect
  // only re-runs on active/facing/device, not on every parent re-render (which would restart it).
  const onDecodeRef = useRef(onDecode);
  onDecodeRef.current = onDecode;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const guideRef = useRef(guide);
  guideRef.current = guide;
  const decodeAreaRef = useRef(decodeArea);
  decodeAreaRef.current = decodeArea;

  const [pickerOpen, setPickerOpen] = useState(false);
  // ⚠ An overlay hides the camera, it does not pause it: nothing counts behind the drawer.
  const heldRef = useRef(false);
  heldRef.current = processing || !!paused || pickerOpen;
  const goneMsRef = useRef(sameCodeGoneMs);
  goneMsRef.current = sameCodeGoneMs;

  const [facing] = useState<"environment" | "user">("environment");
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [detected, setDetected] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  // The preview's size, measured — the guide is DRAWN from the same numbers the decode crop uses.
  const [box, setBox] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Chosen camera deviceId (exact). Seeded from localStorage; validated against the
  // enumerated list once permission is granted.
  const [deviceId, setDeviceId] = useState<string | null>(() => readStoredDeviceId());
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  // The camera actually running: with no choice made, the browser picked it.
  const [runningId, setRunningId] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(scanSoundOn);
  const [restart, setRestart] = useState(0);
  const [flash, setFlash] = useState<ScanSoundKind | null>(null);

  // ⓘ Only a CHANGE rearms: the first render has nothing to forget, and must not start with a pause.
  const lastRearmKey = useRef(rearmKey);

  useEffect(() => {
    if (rearmKey !== lastRearmKey.current) {
      lastRearmKey.current = rearmKey;
      gateRef.current = rearmScanGate(Date.now());
    }
  }, [rearmKey]);

  useEffect(() => {
    if (active) {
      gateRef.current = resumeScanGate(gateRef.current, Date.now());
    }
  }, [active]);

  useEffect(() => {
    // ⚠ Its timer went with the last result (the cleanup below): left on, the mark would stay on the picture.
    if (!result) {
      setFlash(null);

      return undefined;
    }

    setFlash(result.kind);

    const timer = setTimeout(() => setFlash(null), 1200);

    return () => clearTimeout(timer);
  }, [result]);

  // Awake while it runs, and back when the app is: iOS ends the track in the background.
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    let lock: { release: () => Promise<void> } | null = null;
    let gone = false;
    const wake = async () => {
      try {
        const next = await (navigator as unknown as { wakeLock?: { request: (t: string) => Promise<{ release: () => Promise<void> }> } }).wakeLock?.request("screen");

        if (gone) {
          void next?.release();
        } else {
          lock = next ?? null;
        }
      } catch {
        // refused or unsupported: the phone simply sleeps as it would
      }
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      void wake();

      const tracks = streamRef.current?.getVideoTracks() ?? [];

      if (!tracks.length || tracks.some((track) => track.readyState === "ended")) {
        setRestart((n) => n + 1);
      } else {
        videoRef.current?.play().catch(() => undefined);
      }
    };

    void wake();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      gone = true;
      document.removeEventListener("visibilitychange", onVisible);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);

  useEffect(() => {
    const el = containerRef.current;

    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const stop = useCallback(() => {
    if (scanTimerRef.current) {
      clearTimeout(scanTimerRef.current);
      scanTimerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    const video = videoRef.current;
    const attached = (video?.srcObject as MediaStream | null) ?? null;
    attached?.getTracks().forEach((track) => track.stop());
    if (video) {
      video.srcObject = null;
    }

    setTorchSupported(false);
    setTorchOn(false);
  }, []);

  useEffect(() => {
    if (!active) {
      stop();

      return undefined;
    }

    const gen = ++startGenRef.current;
    let cancelled = false;
    const stale = () => cancelled || gen !== startGenRef.current;

    setFailed(null);
    setStarting(true);

    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    const refreshDevices = () => {
      if (!navigator.mediaDevices?.enumerateDevices) {
        return;
      }
      navigator.mediaDevices
        .enumerateDevices()
        .then((all) => {
          if (stale()) {
            return;
          }
          const cams = all.filter((d) => d.kind === "videoinput");
          setDevices(cams);
          if (deviceId && !cams.some((c) => c.deviceId === deviceId)) {
            setDeviceId(null);
            writeStoredDeviceId(null);
          }
        })
        .catch(() => {
          // enumeration failed — keep whatever we have
        });
    };

    const handleHit = (raw: string) => {
      const text = raw.trim();
      if (!text) {
        return;
      }
      const decided = passScanGate(gateRef.current, text, Date.now(), heldRef.current, goneMsRef.current);

      gateRef.current = decided.gate;

      if (!decided.accept) {
        return;
      }

      // Brief green flash so the operator sees the code registered.
      setDetected(true);
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
      flashTimerRef.current = setTimeout(() => setDetected(false), 700);

      onDecodeRef.current(text);
    };

    const run = async () => {
      // ⚠ The decoder is LAZY (barcodeReader.ts): its module and WebAssembly start loading NOW, in
      // parallel with the camera, the first time any scanner opens — not with the app. A rejection is
      // awaited below; the no-op catch only stops it being reported as unhandled if this start goes stale.
      const decoderLoad = loadBarcodeReader();
      decoderLoad.catch(() => undefined);

      try {
        // We drive the stream ourselves so we can guarantee the <video> plays.
        const constraints: MediaStreamConstraints = deviceId
          ? { video: { deviceId: { exact: deviceId }, ...VIDEO_QUALITY } }
          : { video: { facingMode: { ideal: facing }, ...VIDEO_QUALITY } };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (stale()) {
          stream.getTracks().forEach((track) => track.stop());

          return;
        }

        streamRef.current = stream;
        // ⚠ The camera can end with the page still on screen (another app takes it, it is unplugged): a black picture and no
        // message otherwise. `stop()` does not fire this: a track we stop ourselves sends no `ended`.
        stream.getVideoTracks().forEach((track) => {
          track.onended = () => {
            if (!stale() && document.visibilityState === "visible") {
              setRestart((n) => n + 1);
            }
          };
        });
        video.srcObject = stream;
        video.muted = true;
        video.setAttribute("playsinline", "true");
        try {
          await video.play();
        } catch {
          // autoplay can reject; the stream is attached and usually renders anyway
        }
        if (stale()) {
          // ⚠ Release only THIS start's stream. The shared stop() would also tear down a NEWER start
          // that attached while this one's play() was settling (StrictMode, a quick camera switch).
          stream.getTracks().forEach((track) => track.stop());

          if (video.srcObject === stream) {
            video.srcObject = null;
          }

          return;
        }

        setStarting(false);
        refreshDevices();

        // What this camera can do. Every call here is optional hardware — a camera without it
        // simply keeps the defaults, so a refusal is not an error worth showing.
        const track = stream.getVideoTracks()[0];

        setRunningId(track?.getSettings?.().deviceId ?? deviceId);

        const capabilities: any = track?.getCapabilities?.() ?? {};

        if (Array.isArray(capabilities.focusMode) && capabilities.focusMode.includes("continuous")) {
          track.applyConstraints({ advanced: [{ focusMode: "continuous" } as any] }).catch(() => undefined);
        }

        setTorchSupported(!!capabilities.torch);

        let decoder: Awaited<typeof decoderLoad>;

        try {
          decoder = await decoderLoad;
        } catch {
          // ⚠ The decoder could not load — offline before this device ever scanned, or a blocked file.
          // Say so and switch the camera off, rather than show a live preview that can never read.
          if (!stale()) {
            stop();
            setFailed("decoderUnavailable");
            onErrorRef.current?.("decoderUnavailable");
          }

          return;
        }

        if (stale()) {
          return;
        }

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        const variantCanvas = document.createElement("canvas");
        const flattenWork = {
          small: document.createElement("canvas"),
          light: document.createElement("canvas"),
          out: document.createElement("canvas"),
        };
        // Which extra attempt this tick makes (see ATTEMPT_CYCLE), and a short-format reading waiting
        // for its confirming second read.
        let cycle = 0;
        let pending: { text: string; at: number } | null = null;

        /** Decode one prepared canvas; the chosen reading, or null. */
        const readCanvas = async (source: HTMLCanvasElement) => {
          const sourceCtx = source.getContext("2d", { willReadFrequently: true });

          if (!sourceCtx || !source.width || !source.height) {
            return null;
          }

          // The guide is centred, so the middle of what is decoded is where the camera is aimed.
          return pickBarcode(await decoder.readBarcodes(sourceCtx.getImageData(0, 0, source.width, source.height), READER_OPTIONS), {
            x: source.width / 2,
            y: source.height / 2,
          });
        };

        /** One tick: crop the frame to what may be read, then decode that crop plain + one variant. */
        const decodeOnce = async (): Promise<string | null> => {
          const container = containerRef.current;

          if (!ctx || !container || video.readyState < 2 || !video.videoWidth) {
            return null;
          }

          const cw = container.clientWidth;
          const ch = container.clientHeight;
          const region = decodeRegion(cw, ch, guideRef.current, decodeAreaRef.current);
          const crop = previewRectToVideo(region, cw, ch, video.videoWidth, video.videoHeight);

          if (!crop) {
            return null;
          }

          const size = decodeSize(crop, MAX_DECODE_WIDTH);
          canvas.width = size.w;
          canvas.height = size.h;
          ctx.drawImage(video, crop.x, crop.y, crop.w, crop.h, 0, 0, size.w, size.h);

          const extra = ATTEMPT_CYCLE[cycle % ATTEMPT_CYCLE.length];
          cycle += 1;

          let hit = await readCanvas(canvas);

          if (!hit) {
            const variant = extra === "light" ? flattenLighting(canvas, flattenWork) : rotateCanvas(canvas, extra, variantCanvas);
            hit = variant ? await readCanvas(variant) : null;
          }

          const reading = hit ? { format: hit.format, text: normalizeScanText(hit.format, hit.text) } : null;

          gateRef.current = noteScanSighting(gateRef.current, reading?.text, Date.now(), goneMsRef.current);

          const decided = confirmReading(reading, pending, Date.now(), true);
          pending = decided.pending;

          return decided.accept;
        };

        const tick = async () => {
          if (stale()) {
            return;
          }

          const startedAt = performance.now();
          let text: string | null = null;

          try {
            text = await decodeOnce();
          } catch {
            // ⚠ The decoder failed WHILE reading (it loaded, then broke). Say so and stop, rather than
            // scan nothing in silence.
            if (!stale()) {
              // The camera goes off with it — no light burning behind a message saying it cannot scan.
              stop();
              setFailed("decoderUnavailable");
              onErrorRef.current?.("decoderUnavailable");
            }

            return;
          }

          const took = performance.now() - startedAt;

          if (stale()) {
            return;
          }

          if (text) {
            handleHit(text);
          }

          scanTimerRef.current = setTimeout(() => void tick(), Math.max(SCAN_INTERVAL_MS, took * SCAN_BUSY_RATIO));
        };

        void tick();
      } catch (err: any) {
        if (stale()) {
          return;
        }

        const name = err?.name || "";

        // A remembered deviceId that isn't available on THIS device throws — drop it
        // and let the effect re-run with the default camera rather than dead-ending.
        if (deviceId && (name === "OverconstrainedError" || name === "NotFoundError" || name === "DevicesNotFoundError")) {
          setDeviceId(null);
          writeStoredDeviceId(null);

          return;
        }

        setStarting(false);

        let reason = "cameraUnknownError";
        if (typeof window !== "undefined" && window.isSecureContext === false) {
          reason = "cameraInsecure";
        } else if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          reason = "cameraPermissionDenied";
        } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          reason = "cameraNotFound";
        }

        setFailed(reason);
        onErrorRef.current?.(reason);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
      setDetected(false);
      stop();
    };
  }, [active, facing, deviceId, stop, restart]);

  const cameras = useMemo(() => cameraChoices(devices), [devices]);

  const chooseCamera = useCallback((id: string) => {
    writeStoredDeviceId(id);
    setDeviceId(id);
    setRunningId(id);
    setPickerOpen(false);
  }, []);

  // Torch for a shaded shelf or a dim storeroom. Offered only where the camera reports one; if the
  // browser then refuses it after all, the button goes away rather than sitting there doing nothing.
  const toggleTorch = useCallback(() => {
    const track = streamRef.current?.getVideoTracks()[0];

    if (!track) {
      return;
    }

    const next = !torchOn;

    track
      .applyConstraints({ advanced: [{ torch: next } as any] })
      .then(() => setTorchOn(next))
      .catch(() => {
        setTorchOn(false);
        setTorchSupported(false);
      });
  }, [torchOn]);

  const failedMessage = (reason: string): string => {
    if (reason === "cameraInsecure") {
      return t("events:checkin.camera.insecure", {
        defaultValue: "The camera needs a secure (https) connection. Type the code instead.",
      });
    }
    if (reason === "cameraPermissionDenied") {
      return t("events:checkin.camera.denied", {
        defaultValue: "Camera access was blocked. Allow it in your browser, or type the code.",
      });
    }
    if (reason === "decoderUnavailable") {
      return t("events:checkin.camera.decoderUnavailable", {
        defaultValue: "The scanner couldn't load. Check your connection and reopen the camera, or type the code.",
      });
    }
    if (reason === "cameraNotFound") {
      return t("events:checkin.camera.notFound", { defaultValue: "No camera found. Type the code instead." });
    }

    return t("events:checkin.camera.unknown", { defaultValue: "Couldn't start the camera. Type the code instead." });
  };

  const flashColor = flash ? { ok: "teal", info: "blue", warn: "orange", error: "red" }[flash] : null;
  const guideColor = flashColor
    ? `var(--mantine-color-${flashColor}-5)`
    : detected || processing
      ? "var(--mantine-color-teal-5)"
      : "rgba(255,255,255,0.9)";
  // Drawn from the SAME function the decode crop uses. Until the preview is measured (one frame)
  // nothing is drawn rather than a guide in the wrong place.
  const drawnGuide = box.w > 0 && box.h > 0 ? guideRect(box.w, box.h, guide) : null;

  return (
    <Box
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height,
        borderRadius: 12,
        overflow: "hidden",
        background: "#000",
      }}
    >
      <video
        ref={videoRef}
        muted
        autoPlay
        playsInline
        style={{ width: "100%", height: "100%", objectFit: "cover", display: failed ? "none" : "block" }}
      />

      {/* Framing guide (turns green on detect / while looking up) */}
      {!failed && !starting && drawnGuide && (
        <Box
          style={{
            position: "absolute",
            left: drawnGuide.x,
            top: drawnGuide.y,
            width: drawnGuide.w,
            height: drawnGuide.h,
            border: `3px solid ${guideColor}`,
            borderRadius: 16,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
            transition: "border-color 120ms ease",
            pointerEvents: "none",
          }}
        />
      )}

      {/* "Detected — looking up" overlay while the caller resolves the scanned code */}
      {!failed && !starting && processing && (
        <Center style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <Stack align="center" gap={6}>
            <Loader color="teal" size="sm" />
            <Text size="md" fw={600} c="white">
              {t("events:checkin.camera.lookingUp", { defaultValue: "Detected — looking up…" })}
            </Text>
          </Stack>
        </Center>
      )}

      {starting && !failed && (
        <Center style={{ position: "absolute", inset: 0 }}>
          <Stack align="center" gap="xs">
            <Loader color="white" />
            <Text size="md" c="white">
              {t("events:checkin.camera.starting", { defaultValue: "Starting camera…" })}
            </Text>
          </Stack>
        </Center>
      )}

      {failed && (
        <Center style={{ position: "absolute", inset: 0, padding: 16 }}>
          <Stack align="center" gap="xs">
            <AppIcon iconName="CameraOff" size={28} color="white" />
            <Text size="md" c="white" ta="center">
              {failedMessage(failed)}
            </Text>
          </Stack>
        </Center>
      )}

      {/* Torch — only when this camera has one. */}
      {!failed && torchSupported && (
        <Tooltip
          label={
            torchOn
              ? t("events:checkin.camera.torchOff", { defaultValue: "Turn the light off" })
              : t("events:checkin.camera.torchOn", { defaultValue: "Turn the light on" })
          }
          withArrow
        >
          <ActionIcon
            variant="filled"
            color={torchOn ? "yellow" : "dark"}
            radius="xl"
            size="lg"
            onClick={toggleTorch}
            style={{ position: "absolute", top: 10, left: 10, opacity: 0.85 }}
            aria-pressed={torchOn}
            aria-label={
              torchOn
                ? t("events:checkin.camera.torchOff", { defaultValue: "Turn the light off" })
                : t("events:checkin.camera.torchOn", { defaultValue: "Turn the light on" })
            }
          >
            {torchOn ? <AppIcon iconName="BulbOff" size={18} /> : <AppIcon iconName="Bulb" size={18} />}
          </ActionIcon>
        </Tooltip>
      )}

      {/* The result, big enough to read from where the operator is standing. */}
      {!failed && !starting && !processing && flash && (
        <Center style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <Box style={{ background: `var(--mantine-color-${flashColor}-6)`, borderRadius: "50%", padding: 12, display: "flex" }}>
            <AppIcon iconName={flash === "ok" ? "CircleCheck" : flash === "error" ? "CircleX" : flash === "warn" ? "AlertTriangle" : "InfoCircle"} size={44} color="white" />
          </Box>
        </Center>
      )}

      {!failed && !starting && !!paused && (
        <Center style={{ position: "absolute", inset: 0, padding: 16, background: "rgba(0,0,0,0.55)", pointerEvents: "none" }}>
          <Text size="md" fw={600} c="white" ta="center">
            {paused}
          </Text>
        </Center>
      )}

      {children}

      {!failed && cameras.length > 0 && (
        <Tooltip label={t("events:checkin.camera.choose", { defaultValue: "Choose a camera" })} withArrow>
          <ActionIcon
            variant="filled"
            color="dark"
            radius="xl"
            size="lg"
            onClick={() => setPickerOpen(true)}
            style={{ position: "absolute", top: 10, right: 10, opacity: 0.85 }}
            aria-label={t("events:checkin.camera.choose", { defaultValue: "Choose a camera" })}
          >
            <AppIcon iconName="CameraRotate" size={18} />
          </ActionIcon>
        </Tooltip>
      )}

      {/* ⓘ On local state, not a URL stage: this drawer belongs to the scanner wherever it is drawn (four pages, two of them
          inside another drawer), it pins no record and nothing is lost if a reload closes it. */}
      <AppDrawer
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t("events:checkin.camera.choose", { defaultValue: "Choose a camera" })}
        actionType="close"
        mobileFitContent
      >
        <Stack gap="xs">
          {cameras.map((camera) => {
            const inUse = camera.id === runningId;

            return (
              <UnstyledButton
                key={camera.id}
                onClick={() => chooseCamera(camera.id)}
                aria-pressed={inUse}
                style={{
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: `2px solid ${inUse ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-default-border)"}`,
                  background: inUse ? "var(--mantine-primary-color-light)" : undefined,
                }}
              >
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Text size="md" fw={inUse ? 700 : 500}>
                    {camera.label}
                  </Text>
                  {inUse ? (
                    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Text size="md" fw={600} c="var(--mantine-primary-color-filled)">
                        {t("events:checkin.camera.inUse", { defaultValue: "In use" })}
                      </Text>
                      <AppIcon iconName="CircleCheck" size={20} color="var(--mantine-primary-color-filled)" />
                    </Group>
                  ) : null}
                </Group>
              </UnstyledButton>
            );
          })}
          {soundSwitch ? (
            <Switch
              mt="sm"
              size="md"
              checked={soundOn}
              onChange={(e) => {
                const next = e.currentTarget.checked;

                setScanSoundOn(next);
                setSoundOn(next);
              }}
              label={t("events:checkin.camera.sound", { defaultValue: "Beep on each scan" })}
            />
          ) : null}
        </Stack>
      </AppDrawer>
    </Box>
  );
};
